import React, { useEffect, useState } from "react";
import { getCurrencySymbol } from "../../../../../tenant/currency";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styles from "./buy-electricity-form.module.scss";

import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../stores/store";
import { useLazyVerifyPostpaidMeterQuery, useLazyVerifyPrepaidMeterQuery } from "../../../../../service/vending-service";
import { Button } from "../../../components";
import { VerifyMeterNumberPayload, VerifyPostpaidMeterResponse, VerifyPrepaidMeterResponse } from "../../../../../model/charge";
import { getErrorMessage } from "../../../../../utils/error-handler";
import { CountryCode, countryCurrencies } from "../../../../../model/common/country";
import {
	BorrowEligibilityData,
	BuyElectricityConfirmationData,
	BuyElectricityFormData,
	BuyElectricityFormState,
	MeterValidationResponse,
	PaymentType,
} from "../../buy-electricity.types";
import { useGetPaymentFeeMutation } from "../../../../../service/charge-service";
import { describeError } from "../../../../../services/business-analytics/customFields";
import { trackMeterValidationFailed, trackMeterValidationSuccess } from "../../../../../services/business-analytics/vendEvents";
import { formatCurrency } from "../../../../../utils";
import { ConsumerType } from "../../../../../model/energy-profile";
import { BeneficiaryItem } from "../../../../../service/beneficiary-service";
import { MeterSelector } from "../../../components";
import { NumericFormat } from "react-number-format";
import { useFeatureFlag } from "../../../../../tenant/features/useFeatureFlag";
import { FeatureFlagName } from "../../../../../tenant/features/featureFlags";

interface BuyElectricityFormProps {
	countryCode: CountryCode;
	onCancel: () => void;
	onContinue: (data: BuyElectricityConfirmationData, borrowEligibility?: BorrowEligibilityData) => void;
	initialFormState?: BuyElectricityFormState;
	onFormStateChange?: (state: BuyElectricityFormState) => void;
}

const currencySymbol = (currency: string) => getCurrencySymbol(currency);

const AMOUNT_PRESETS = [5000, 10000, 20000, 50000, 100000, 200000];


export const BuyElectricityForm: React.FC<BuyElectricityFormProps> = ({
	countryCode,
	onCancel,
	onContinue,
	initialFormState,
	onFormStateChange,
}) => {
	const { t } = useTranslation();
	const energyProfile = useSelector((state: RootState) => state?.customerEnergyProfile);

	const [paymentType, setPaymentType] = useState<PaymentType>(initialFormState?.paymentType || null);
	const [meterValidationData, setMeterValidationData] = useState<MeterValidationResponse | null>(
		initialFormState?.meterValidationData || null
	);
	const [meterValidationError, setMeterValidationError] = useState<string>("");
	const [isValidatingMeter, setIsValidatingMeter] = useState(false);
	const [fullMeterResponse, setFullMeterResponse] = useState<
		VerifyPrepaidMeterResponse | VerifyPostpaidMeterResponse | null
	>(null);
	const [getPaymentFee, { isLoading: isCalculatingFee }] = useGetPaymentFeeMutation();
	const [verifyPrepaidMeter] = useLazyVerifyPrepaidMeterQuery();
	const [verifyPostpaidMeter] = useLazyVerifyPostpaidMeterQuery();

	const {
		control,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { errors },
	} = useForm<BuyElectricityFormData>({
		mode: "onChange",
		defaultValues: {
			meterNumber: initialFormState?.meterNumber || "",
			amount: initialFormState?.amount || "",
		},
	});

	const watchedMeterNumber = watch("meterNumber");
	const watchedAmount = watch("amount");
	const parsedAmount = Number.parseFloat(watchedAmount || "0");

	useEffect(() => {
		if (onFormStateChange) {
			onFormStateChange({
				paymentType,
				meterNumber: watchedMeterNumber,
				amount: watchedAmount,
				meterValidationData,
				showForm: paymentType !== null,
			});
		}
	}, [paymentType, watchedMeterNumber, watchedAmount, meterValidationData, onFormStateChange]);

	const handleSelectPaymentType = (next: PaymentType) => {
		if (next === paymentType) return;
		setPaymentType(next);
		reset({ meterNumber: "", amount: "" });
		setMeterValidationData(null);
		setMeterValidationError("");
		setFullMeterResponse(null);
	};

	const handleBeneficiarySelect = (b: BeneficiaryItem) => {
		const inferred: PaymentType =
			b.type === ConsumerType.Prepaid
				? "prepaid"
				: b.type === ConsumerType.Postpaid
					? "postpaid"
					: null;
		if (inferred) setPaymentType(inferred);
		setMeterValidationData(null);
		setMeterValidationError("");
		setFullMeterResponse(null);
		reset({ meterNumber: b.identifier, amount: "" });
	};

	const validateMeter = async (meterNumber: string): Promise<MeterValidationResponse | null> => {
		if (!paymentType) return null;

		setIsValidatingMeter(true);
		setMeterValidationError("");

		try {
			const payload: VerifyMeterNumberPayload = {
				MeterNumber: meterNumber,
				UtilityId: energyProfile.utilityId ?? "",
			};

			let response: VerifyPrepaidMeterResponse | VerifyPostpaidMeterResponse;

			if (paymentType === "prepaid") {
				response = await verifyPrepaidMeter(payload).unwrap();
			} else {
				response = await verifyPostpaidMeter(payload).unwrap();
			}

			setFullMeterResponse(response);

			const validationData: MeterValidationResponse = {
				customerName: response.accountName,
				outstandingBalance: (response as VerifyPostpaidMeterResponse).outstandingBalance ?? undefined,
			};

			setMeterValidationData(validationData);
			trackMeterValidationSuccess(meterNumber, paymentType);
			return validationData;
		} catch (error: any) {
			trackMeterValidationFailed(meterNumber, describeError(error));
			const errorMessage = getErrorMessage(error?.data) || t("buyElectricity.web.meterNumberDoesNotExist");
			setMeterValidationError(errorMessage);
			setMeterValidationData(null);
			setFullMeterResponse(null);
			return null;
		} finally {
			setIsValidatingMeter(false);
		}
	};

	// Auto-validate meter when user enters 9+ digits (debounced 600ms)
	const [lastValidatedMeter, setLastValidatedMeter] = useState("");
	useEffect(() => {
		if (!paymentType) return;
		if (watchedMeterNumber.length < 9) {
			if (meterValidationData || meterValidationError) {
				setMeterValidationData(null);
				setMeterValidationError("");
				setFullMeterResponse(null);
				setLastValidatedMeter("");
			}
			return;
		}
		if (watchedMeterNumber === lastValidatedMeter) return;

		const timer = setTimeout(() => {
			setLastValidatedMeter(watchedMeterNumber);
			validateMeter(watchedMeterNumber);
		}, 600);

		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [watchedMeterNumber, paymentType]);


	const onSubmit = (data: BuyElectricityFormData) => {
		if (!paymentType) {
			toast.error(t("buyElectricity.web.pleaseSelectPaymentType"));
			return;
		}
		if (!meterValidationData || !fullMeterResponse) {
			toast.error(t("buyElectricity.web.pleaseVerifyMeterFirst"));
			return;
		}

		const amount = Number.parseInt(data.amount, 10);
		if (amount < fullMeterResponse.minimumVendAmount) {
			toast.error(t("buyElectricity.web.minimumVendAmountIs", { amount: formatCurrency(fullMeterResponse.minimumVendAmount) }));
			return;
		}

		getPaymentFee({ amount, currency: countryCurrencies[countryCode] })
			.unwrap()
			.then(res => {
				const confirmationData: BuyElectricityConfirmationData = {
					paymentType,
					meterNumber: data.meterNumber,
					amount: data.amount,
					customerName: meterValidationData.customerName,
					meterAddress: (fullMeterResponse as VerifyPostpaidMeterResponse).address ?? undefined,
					phoneNumber: "",
					outstandingBalance: meterValidationData.outstandingBalance,
					outstandingDebt: (fullMeterResponse as VerifyPrepaidMeterResponse).outstandingDebt ?? 0,
					disco: "",
					fee: res.estimationFees[0]?.paymentChannels[0]?.totalFee?.amount ?? 0,
					currency: countryCurrencies[countryCode],
					showPreferRemoteTokenLoadOption:
						paymentType === "prepaid"
							? (fullMeterResponse as VerifyPrepaidMeterResponse).showPreferRemoteTokenLoadOption ?? false
							: false,
				};

				const borrowEligibility: BorrowEligibilityData | undefined =
					paymentType === "prepaid"
						? {
								minimumBorrowAmount: (fullMeterResponse as VerifyPrepaidMeterResponse).minimumBorrowAmount ?? 0,
								maximumBorrowAmount: (fullMeterResponse as VerifyPrepaidMeterResponse).maximumBorrowAmount ?? 0,
								minimumVendAmount: fullMeterResponse.minimumVendAmount,
								outstandingDebt: fullMeterResponse.outstandingDebt,
							}
						: undefined;

				onContinue(confirmationData, borrowEligibility);
			})
			.catch(err => toast.error(getErrorMessage(err?.data)));
	};

	const showMeterCard = paymentType !== null;
	const showAmountCard = !!meterValidationData && !!fullMeterResponse;
	const continueEnabled = showAmountCard && parsedAmount > 0 && !isCalculatingFee;

	const currencyCode = countryCurrencies[countryCode as CountryCode];
	const sym = currencySymbol(currencyCode);

	const outstandingAmount = fullMeterResponse
		? paymentType === "prepaid"
			? (fullMeterResponse as VerifyPrepaidMeterResponse).outstandingDebt
			: (fullMeterResponse as VerifyPostpaidMeterResponse).outstandingBalance
		: 0;
	const outstandingDebtDisplayOn = useFeatureFlag(FeatureFlagName.OUTSTANDING_DEBT_DISPLAY);
	const showOutstandingBanner = outstandingDebtDisplayOn && !!fullMeterResponse && (outstandingAmount ?? 0) > 0;

	return (
		<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
			<div className={styles.scrollContent}>
				<div className={styles.cards}>
				{/* CARD 1 + CARD 2 — Meter Type + Meter Number */}
				<Controller
					name="meterNumber"
					control={control}
					rules={{
						required: t("buyElectricity.web.meterNumberRequired"),
						minLength: { value: 9, message: t("buyElectricity.web.meterNumberMinDigits") },
						maxLength: { value: 18, message: t("buyElectricity.web.meterNumberMaxDigits") },
					}}
					render={({ field }) => (
						<MeterSelector
							value={paymentType === "prepaid" ? ConsumerType.Prepaid : paymentType === "postpaid" ? ConsumerType.Postpaid : null}
							onTypeChange={t => handleSelectPaymentType(t === ConsumerType.Prepaid ? "prepaid" : "postpaid")}
							meterValue={field.value}
							onMeterChange={field.onChange}
							onBeneficiarySelect={handleBeneficiarySelect}
							isValidating={isValidatingMeter}
							validationData={meterValidationData}
							validationError={meterValidationError}
							currencySymbol={sym}
							outstandingAmount={showOutstandingBanner ? outstandingAmount ?? 0 : undefined}
						/>
					)}
				/>

				{/* CARD 3 — Amount (only after meter verified) */}
				{showAmountCard && (
					<section className={styles.card}>
						<header className={styles.cardHeader}>
							<span className={styles.cardLabel}>{t("common.labels.amount")}</span>
						</header>
						<div className={styles.cardBodyWide}>
							<div className={styles.amountPresets}>
								{AMOUNT_PRESETS.map(preset => {
									const active = parsedAmount === preset;
									return (
										<button
											type="button"
											key={preset}
											className={`${styles.amountChip} ${active ? styles.amountChipActive : ""}`}
											onClick={() => setValue("amount", preset.toString(), { shouldValidate: true })}
										>
											{sym}
											{preset.toLocaleString()}
										</button>
									);
								})}
							</div>
							<div className={styles.amountDivider} />
							<label className={styles.customAmountLabel}>{t("common.labels.customAmount")}</label>
							<div className={styles.customAmountField}>
								<Controller
									name="amount"
									control={control}
									rules={{
										required: t("buyElectricity.web.amountRequired"),
										validate: {
											positive: value => {
												const num = Number.parseFloat(value);
												return num > 0 || t("buyElectricity.web.amountGreaterThanZero");
											},
										},
									}}
									render={({ field }) => (
										<>
											<span className={styles.customAmountPrefix}>{sym}</span>
											<NumericFormat
												value={field.value}
												onValueChange={values => field.onChange(values.value)}
												onBlur={field.onBlur}
												thousandSeparator=","
												decimalSeparator="."
												decimalScale={2}
												allowNegative={false}
												placeholder="0.00"
												inputMode="decimal"
												className={styles.customAmountInput}
											/>
											<div className={styles.customAmountSuffix}>
												<span>{currencyCode}</span>
												<svg width="14" height="14" viewBox="0 0 24 24" fill="none">
													<path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
												</svg>
											</div>
										</>
									)}
								/>
							</div>
							{errors.amount?.message && (
								<span className={styles.amountError}>{errors.amount.message}</span>
							)}
						</div>
					</section>
				)}
				</div>
			</div>

			<footer className={styles.footer}>
				<Button
					type="submit"
					variant="primary"
					disabled={!continueEnabled}
					fullWidth
					className={styles.continueButton}
				>
					{isCalculatingFee ? t("buyElectricity.web.calculatingFee") : t("common.actions.continueKey")}
				</Button>
			</footer>
		</form>
	);
};
